/**
 * API请求封装
 */
import axios from 'axios';
import { getAuthHeader, logout, getToken } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// 初始化时从localStorage加载token到api实例
const token = getToken();
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// 请求拦截器：自动添加认证头
api.interceptors.request.use(
  (config) => {
    const authHeader = getAuthHeader();
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization;
    }
    // Carry the active UI language so the backend can localize AI output for
    // endpoints without a body `language` field (upload, stage nav, speech, TTS).
    // Key mirrors i18n/index.js LANGUAGE_STORAGE_KEY ('graphTutor::lang').
    try {
      const lang = localStorage.getItem('graphTutor::lang');
      config.headers['X-App-Language'] = lang === 'en' ? 'en' : 'ja';
    } catch (e) {
      config.headers['X-App-Language'] = 'ja';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理401错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token过期或无效，自动登出(但不在登录/注册页面)
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        console.log('401 error, logging out from path:', currentPath);
        logout();
      }
    }
    return Promise.reject(error);
  }
);

// ==================== 认证API ====================

export const authAPI = {
  // 注册
  register: (data) => api.post('/api/auth/register', data),

  // 登录
  login: (data) => api.post('/api/auth/login', data),

  // 获取当前用户信息
  getCurrentUser: () => api.get('/api/auth/me'),

  // 更新设置
  updateSettings: (settings) => api.put('/api/auth/settings', settings),

  // 获取设置
  getSettings: () => api.get('/api/auth/settings'),
};

// ==================== 会话API ====================

export const sessionAPI = {
  // 创建新会话
  create: (data) => api.post('/api/sessions/', data),

  // 获取所有会话
  getAll: () => api.get('/api/sessions/'),

  // 获取会话详情
  get: (sessionId) => api.get(`/api/sessions/${sessionId}`),

  // 更新会话
  update: (sessionId, data) => api.put(`/api/sessions/${sessionId}`, data),

  // 删除会话
  delete: (sessionId) => api.delete(`/api/sessions/${sessionId}`),

  // 添加消息
  addMessage: (sessionId, message) => api.post(`/api/sessions/${sessionId}/messages`, message),

  // 获取消息列表
  getMessages: (sessionId) => api.get(`/api/sessions/${sessionId}/messages`),
};

// ==================== TTS API ====================

export const ttsAPI = {
  // 合成语音
  synthesize: (data) => api.post('/api/tts/synthesize', data),
};

// ==================== 問題統合 API ====================

export const problemsAPI = {
  upload: ({ file, problemText, graphId, sessionId }) => {
    const formData = new FormData();
    formData.append('image', file);
    if (problemText) formData.append('problem_text', problemText);
    if (graphId) formData.append('graph_id', graphId);
    if (sessionId) formData.append('session_id', String(sessionId));
    return api.post('/api/problems/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 210000,
    });
  },
  getHints: (problemId) => api.get(`/api/problems/${problemId}/hints`),
  guide: (problemId, data) => api.post(`/api/problems/${problemId}/guide`, data),
  attachSession: (problemId, data) => api.post(`/api/problems/${problemId}/attach-session`, data),
  getBySession: (sessionId) => api.get(`/api/problems/by-session/${sessionId}`),
};

// ==================== 教師プリセット API ====================

export const teacherAPI = {
  getPresets: () => api.get('/api/teacher/presets/'),
  createPreset: (data) => api.post('/api/teacher/presets/', data),
  updatePreset: (id, data) => api.put('/api/teacher/presets/' + id, data),
  deletePreset: (id) => api.delete('/api/teacher/presets/' + id),
  setDefaultPreset: (id) => api.post('/api/teacher/presets/' + id + '/set-default'),
  // 分析ダッシュボード
  getAnalyticsStudents: (offset = 0, limit = 50) => api.get(`/api/analytics/students?offset=${offset}&limit=${limit}`),
  getStudentSummary: (studentId) => api.get(`/api/analytics/students/${studentId}/summary`),
  // P1-2: AI分析の品質指標 (chart routing telemetry aggregate)
  getModelABSummary: () => api.get('/api/analytics/model-ab-summary'),
  // 生徒管理
  generateInviteCode: () => api.post('/api/teacher/invite-code'),
  getInviteCode: () => api.get('/api/teacher/invite-code'),
  regenerateInviteCode: () => api.post('/api/teacher/invite-code/regenerate'),
  getMyStudents: () => api.get('/api/teacher/students'),
  removeStudent: (id) => api.delete('/api/teacher/students/' + id),
};

// ==================== Analytics API ====================

export const analyticsAPI = {
  batchActionLogs: (records) => api.post('/api/analytics/action-logs', { records }),
  batchEscalationLogs: (records) => api.post('/api/analytics/escalation-logs', { records }),
  // T5: DBR (Design-Based Research) 形成的評価ダッシュボード（教師専用、GET /api/analytics/dbr-summary）
  getDbrSummary: () => api.get('/api/analytics/dbr-summary'),
};

// ==================== Phase A research instrumentation（既定 OFF・生徒のみ） ====================
// Server-enforced flags: the backend never stores research-only events for a user whose
// flags are off, so these calls are harmless even if a stale client makes them.
export const researchAPI = {
  getFeatures: () => api.get('/api/research/features'),
  postTaskEvent: (event) => api.post('/api/research/task-events', event),
};

// ==================== 生徒クラス参加 API ====================

export const studentAPI = {
  joinClass: (code, force = false) => api.post('/api/student/join', { code, force }),
  getMyTeacher: () => api.get('/api/student/my-teacher'),
  leaveClass: () => api.delete('/api/student/leave'),
};

// ==================== Realtime Voice Tutor API (P0-4 Phase 2) ====================
// Backend: POST /api/realtime/session (ephemeral token + session id resolve)
//          POST /api/realtime/tool    (Layer 1/2 tool dispatch + audit)
// JWT auto-attached by axios interceptor above — DO NOT include `ephemeral_token`
// in body or URL; the token is held in `useRealtimeVoice.ephemeralTokenRef` only
// and used directly against OpenAI Realtime API for SDP exchange.
export const realtimeAPI = {
  createSession: (data) => api.post('/api/realtime/session', data),
  dispatchTool: (data) => api.post('/api/realtime/tool', data),
};

export default api;
